import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Church, ArrowRight, Calendar, HandCoins, BookOpen, Video, Users, Heart, Play, ChevronRight, Mail, Flame } from 'lucide-react';
import api, { mediaUrl } from '@/lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PublicWebsite() {
  const navigate = useNavigate();
  const [churchProfile, setChurchProfile] = useState({});
  const [events, setEvents] = useState([]);
  const [sermons, setSermons] = useState([]);
  const [dailyVerse, setDailyVerse] = useState(null);
  const [prophecies, setProphecies] = useState([]);
  const [cms, setCms] = useState({});
  const [seoData, setSeoData] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, verseRes, prophecyRes, sermonsRes, eventsRes, cmsRes, seoRes, cssRes, analyticsRes] = await Promise.all([
          fetch(`${API_BASE}/settings/church-profile`).then(r => r.json()),
          fetch(`${API_BASE}/bible/daily-verse`).then(r => r.json()),
          fetch(`${API_BASE}/prophecy/wall`).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE}/public/sermons`).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE}/public/events`).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE}/cms/public`).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE}/cms/seo`).then(r => r.json()).catch(() => ({})),
          fetch(`${API_BASE}/cms/custom-css`).then(r => r.json()).catch(() => ({})),
          fetch(`${API_BASE}/cms/analytics`).then(r => r.json()).catch(() => ({})),
        ]);
        setChurchProfile(profileRes);
        setDailyVerse(verseRes);
        setProphecies(prophecyRes || []);
        setSermons(sermonsRes || []);
        setEvents(eventsRes || []);
        const cmsMap = {};
        (cmsRes || []).forEach(s => { cmsMap[s.section_id] = s; });
        setCms(cmsMap);
        setSeoData(seoRes || {});
        
        // Apply SEO meta tags
        if (seoRes?.page_title) document.title = seoRes.page_title;
        if (seoRes?.meta_description) {
          let meta = document.querySelector('meta[name="description"]');
          if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
          meta.content = seoRes.meta_description;
        }
        if (seoRes?.og_title) {
          let og = document.querySelector('meta[property="og:title"]');
          if (!og) { og = document.createElement('meta'); og.setAttribute('property', 'og:title'); document.head.appendChild(og); }
          og.content = seoRes.og_title;
        }
        if (seoRes?.og_description) {
          let og = document.querySelector('meta[property="og:description"]');
          if (!og) { og = document.createElement('meta'); og.setAttribute('property', 'og:description'); document.head.appendChild(og); }
          og.content = seoRes.og_description;
        }
        if (seoRes?.og_image) {
          let og = document.querySelector('meta[property="og:image"]');
          if (!og) { og = document.createElement('meta'); og.setAttribute('property', 'og:image'); document.head.appendChild(og); }
          og.content = seoRes.og_image;
        }
        
        // Inject custom CSS
        if (cssRes?.css) {
          let style = document.getElementById('cms-custom-css');
          if (!style) { style = document.createElement('style'); style.id = 'cms-custom-css'; document.head.appendChild(style); }
          style.textContent = cssRes.css;
        }
        
        // Inject analytics
        if (analyticsRes?.google_analytics_id) {
          const gaId = analyticsRes.google_analytics_id;
          if (!document.getElementById('ga-script')) {
            const s1 = document.createElement('script'); s1.id = 'ga-script'; s1.async = true;
            s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`; document.head.appendChild(s1);
            const s2 = document.createElement('script');
            s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
            document.head.appendChild(s2);
          }
        }
        if (analyticsRes?.facebook_pixel_id) {
          const fbId = analyticsRes.facebook_pixel_id;
          if (!document.getElementById('fb-pixel')) {
            const s = document.createElement('script'); s.id = 'fb-pixel';
            s.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${fbId}');fbq('track','PageView');`;
            document.head.appendChild(s);
          }
        }
        if (analyticsRes?.custom_head_script) {
          const div = document.createElement('div');
          div.innerHTML = analyticsRes.custom_head_script;
          Array.from(div.children).forEach(el => document.head.appendChild(el.cloneNode(true)));
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  // CMS helper - get section data, fallback to defaults
  const cmsSection = (id) => cms[id] || {};
  const cmsEnabled = (id) => cms[id]?.enabled !== false; // Default true if no CMS

  // Build an inline background style from a section's background_url / settings.bg_image
  const cmsBgStyle = (id) => {
    const s = cmsSection(id);
    const bg = s.background_url || s.settings?.bg_image;
    if (!bg) return undefined;
    return {
      backgroundImage: `linear-gradient(rgba(10,10,10,0.82),rgba(10,10,10,0.82)), url(${mediaUrl(bg)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  };

  // Admin-added custom sections, sorted by their order field
  const customSections = Object.values(cms)
    .filter((s) => s && (s.section_id || '').startsWith('custom_') && s.enabled !== false && (s.content || s.title))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const churchName = cmsSection('hero').title || churchProfile.church_name || 'NaziriteOS Church';
  const motto = cmsSection('hero').subtitle || churchProfile.church_motto || 'A place of worship, community, and growth';
  const heroStats = cmsSection('hero').items || [
    { value: '500+', label: 'Members' },
    { value: '52', label: 'Weekly Services' },
    { value: '24/7', label: 'Prayer Line' },
  ];

  // Give CTA copy - CMS driven with hardcoded fallback
  const giveTitle = cmsSection('give').title || 'Support Our Mission';
  const giveSubtitle = cmsSection('give').subtitle || cmsSection('give').content ||
    'Your generous giving helps support worship services, community outreach, youth programs, and missions around the world.';

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Church className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-lg tracking-tight">{churchName}</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/60">
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <a href="#events" className="hover:text-white transition-colors">Events</a>
            <a href="#sermons" className="hover:text-white transition-colors">Sermons</a>
            <a href="#give" className="hover:text-white transition-colors">Give</a>
            <a href="#prophecy" className="hover:text-white transition-colors">Prophecy</a>
            <a href="#verse" className="hover:text-white transition-colors">Daily Verse</a>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/staff-login')}
              className="text-sm text-white/50 hover:text-white transition-colors hidden sm:inline"
              data-testid="public-staff-login-link"
            >
              Staff Portal
            </button>
            <Button
              onClick={() => navigate('/login')}
              className="bg-white text-black hover:bg-white/90 rounded-full px-5 h-9 text-sm font-medium"
              data-testid="public-login-btn"
            >
              Member Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Animated */}
      <section className="relative min-h-screen flex items-center pt-16" style={cmsBgStyle('hero')}>
        {/* Gradient background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-amber-500/10 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-teal-500/8 blur-[100px]" />
          <div className="absolute top-[30%] right-[20%] w-[300px] h-[300px] rounded-full bg-amber-600/5 blur-[80px]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <Badge className="bg-white/10 text-white/80 border-white/10 rounded-full px-4 py-1.5 text-xs font-normal backdrop-blur-sm">
              Welcome to our church community
              <ChevronRight className="w-3 h-3 ml-1 inline" />
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.95] mb-6"
          >
            <span className="bg-gradient-to-r from-white via-white/90 to-white/40 bg-clip-text text-transparent">
              {churchName}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/50 max-w-lg leading-relaxed mb-10"
          >
            {motto}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button
              onClick={() => navigate('/login')}
              className="bg-white text-black hover:bg-white/90 rounded-full px-8 h-12 text-sm font-semibold"
              data-testid="hero-get-started-btn"
            >
              Get Started
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 h-12 text-sm font-medium"
            >
              View Events
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex gap-12 mt-20"
          >
            {heroStats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-white/40 mt-1 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      {cmsEnabled('about') && (
      <section id="about" className="py-24 relative" style={cmsBgStyle('about')}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{cmsSection('about').title || 'Everything your church needs'}</h2>
            <p className="text-white/50 max-w-md mx-auto">{cmsSection('about').subtitle || 'A complete platform for worship, community, and growth'}</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Calendar, title: 'Events & Services', desc: 'Plan services, manage events, and track attendance with QR check-in' },
              { icon: HandCoins, title: 'Tithes & Giving', desc: 'Online giving via Stripe & Paystack with donor analytics and reporting' },
              { icon: BookOpen, title: 'Bible & Devotional', desc: 'Daily verse, Bible search, bookmarks, and AI study guides' },
              { icon: Video, title: 'Live Streaming', desc: 'Stream services live via Agora with real-time video calls' },
              { icon: Users, title: 'Member Management', desc: 'Complete member directory, families, groups, and engagement scoring' },
              { icon: Heart, title: 'Pastoral Care', desc: 'Prayer requests, consultations, follow-ups, and automated alerts' },
            ].map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Card className="bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06] transition-all duration-300 h-full">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center mb-4">
                      <f.icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{f.title}</h3>
                    <p className="text-white/40 text-sm leading-relaxed">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Daily Verse */}
      {dailyVerse && cmsEnabled('verse') && (
        <section id="verse" className="py-24 relative" style={cmsBgStyle('verse')}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
              <div className="inline-flex items-center gap-2 text-amber-400 text-sm mb-6">
                <BookOpen className="w-4 h-4" />
                Daily Verse
              </div>
              <blockquote className="text-2xl sm:text-3xl font-light leading-relaxed text-white/80 italic mb-6">
                "{dailyVerse.text?.trim()}"
              </blockquote>
              <cite className="text-amber-400 font-semibold not-italic">{dailyVerse.reference}</cite>
              {dailyVerse.reflection && (
                <p className="text-white/50 mt-8 text-base leading-relaxed max-w-xl mx-auto">{dailyVerse.reflection}</p>
              )}
              {dailyVerse.prayer && (
                <div className="mt-6 p-6 bg-white/[0.03] border border-white/[0.06] rounded-xl max-w-lg mx-auto">
                  <p className="text-amber-400/60 text-xs font-medium uppercase tracking-wider mb-2">Today's Prayer</p>
                  <p className="text-white/40 text-sm italic leading-relaxed">{dailyVerse.prayer}</p>
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Event Calendar */}
      {events.length > 0 && cmsEnabled('events') && (
        <section id="events" className="py-24 relative" style={cmsBgStyle('events')}>
          <div className="max-w-6xl mx-auto px-6">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-amber-400 text-sm mb-4"><Calendar className="w-4 h-4" /> Upcoming</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{cmsSection('events').title || 'Events & Services'}</h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-4">
              {events.slice(0, 6).map((e, i) => (
                <motion.div key={e.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <Card className="bg-white/[0.03] border-white/[0.06]">
                    <CardContent className="p-5">
                      <div className="text-amber-400 text-xs font-medium mb-2">{e.start_date}</div>
                      <h3 className="text-white font-semibold mb-1">{e.title}</h3>
                      {e.location && <p className="text-white/40 text-sm">{e.location}</p>}
                      {e.description && <p className="text-white/30 text-xs mt-2 line-clamp-2">{e.description}</p>}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sermon Archive */}
      {sermons.length > 0 && cmsEnabled('sermons') && (
        <section id="sermons" className="py-24 relative" style={cmsBgStyle('sermons')}>
          <div className="max-w-6xl mx-auto px-6">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-amber-400 text-sm mb-4"><Video className="w-4 h-4" /> Sermons</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{cmsSection('sermons').title || 'Recent Sermons'}</h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sermons.slice(0, 6).map((s, i) => (
                <motion.div key={s.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <Card className="bg-white/[0.03] border-white/[0.06]">
                    <CardContent className="p-5">
                      <h3 className="text-white font-semibold mb-1">{s.title}</h3>
                      <p className="text-white/50 text-sm">{s.preacher} | {s.date}</p>
                      {s.series && <Badge className="mt-2 bg-white/10 text-white/60 border-0">{s.series}</Badge>}
                      {s.youtube_url && <a href={s.youtube_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-amber-400 text-xs mt-3 hover:underline"><Play className="w-3 h-3" /> Watch</a>}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Prophecy Wall */}
      {prophecies.length > 0 && cmsEnabled('prophecy') && (
        <section id="prophecy" className="py-24 relative" style={cmsBgStyle('prophecy')}>
          <div className="max-w-6xl mx-auto px-6">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-amber-400 text-sm mb-4">
                <Flame className="w-4 h-4" />
                Prophetic Words
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Words from the Lord</h2>
              <p className="text-white/50 max-w-md mx-auto">Prophetic words shared and confirmed by our pastoral team</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {prophecies.slice(0, 4).map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <Card className="bg-white/[0.03] border-white/[0.06] h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Flame className="w-4 h-4 text-amber-400" />
                        <h3 className="text-white font-semibold">{p.title}</h3>
                      </div>
                      <p className="text-white/50 text-sm leading-relaxed line-clamp-4 italic">"{p.content}"</p>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                        <span className="text-xs text-white/30">{p.author_name}</span>
                        <div className="flex items-center gap-1 text-xs text-amber-400/60">
                          <Heart className="w-3 h-3" /> {p.likes || 0}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Give Section */}
      {cmsEnabled('give') && (
      <section id="give" className="py-24 relative" style={cmsBgStyle('give')}>
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Card className="bg-gradient-to-br from-amber-500/10 to-teal-500/5 border-amber-500/20 overflow-hidden">
              <CardContent className="p-10 sm:p-16 text-center relative">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-[80px]" />
                <HandCoins className="w-12 h-12 text-amber-400 mx-auto mb-6" />
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{giveTitle}</h2>
                <p className="text-white/50 max-w-md mx-auto mb-8 leading-relaxed">
                  {giveSubtitle}
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-amber-500 hover:bg-amber-400 text-black rounded-full px-8 h-12 text-sm font-semibold"
                  data-testid="public-give-btn"
                >
                  Give Online
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
      )}

      {/* Admin-added custom CMS sections (rich text) */}
      {customSections.map((s) => (
        <section key={s.section_id} id={s.section_id} className="py-24 relative" style={cmsBgStyle(s.section_id)}>
          <div className="max-w-4xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
              {s.title && <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{s.title}</h2>}
              {s.subtitle && <p className="text-white/50 max-w-md mx-auto mb-8">{s.subtitle}</p>}
              {s.content && (
                <div
                  className="prose prose-invert max-w-3xl mx-auto text-white/70 leading-relaxed text-left"
                  dangerouslySetInnerHTML={{ __html: s.content }}
                />
              )}
            </motion.div>
          </div>
        </section>
      ))}

      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                  <Church className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="font-semibold">{churchName}</span>
              </div>
              <p className="text-white/30 text-sm leading-relaxed">{motto}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-3 text-white/60">Contact</h4>
              <div className="space-y-2 text-sm text-white/30">
                {churchProfile.church_address && <p>{churchProfile.church_address}</p>}
                {churchProfile.church_phone && <p>{churchProfile.church_phone}</p>}
                {churchProfile.church_email && <p>{churchProfile.church_email}</p>}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-3 text-white/60">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="#about" className="block text-white/30 hover:text-white/60 transition-colors">About Us</a>
                <a href="#events" className="block text-white/30 hover:text-white/60 transition-colors">Events</a>
                <a href="#give" className="block text-white/30 hover:text-white/60 transition-colors">Online Giving</a>
                <button onClick={() => navigate('/login')} className="block text-white/30 hover:text-white/60 transition-colors">Member Portal</button>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-6 text-center text-xs text-white/20">
            Powered by NaziriteOS Church Management System
          </div>
        </div>
      </footer>
    </div>
  );
}
